import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { Folder, ChevronDown, Check } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/Command';
import { getContracts } from './contractPickerService';
import { setContract } from '@/store/actions/contract';

interface ContractResult {
  title: string;
  phase: string;
  contract: string;
  model: string;
}

export default function ContractPicker() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ContractResult[]>([]);
  const [loading, setLoading] = useState(false);
  const { phaseId, contract } = useSelector((state: any) => state.contract);
  const dispatch = useDispatch();

  const fetchContracts = async (term: string) => {
    try {
      setLoading(true);
      const res = await getContracts(term);
      setItems(res.data.results);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((term: string) => fetchContracts(term), 400),
    []
  );

  useEffect(() => {
    if (open) {
      fetchContracts('');
    }
  }, [open]);

  const handleSelect = async (item: ContractResult) => {
    try {
      dispatch(
        setContract({
          contract: { id: item.contract, title: item.title },
          model: item.model,
          phase: item.phase,
        })
      );
      setOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          data-testid="contract-picker-trigger-btn"
          className="h-9 px-3 inline-flex items-center gap-2 rounded-md bg-chrome-search-bg border border-chrome-search-border hover:bg-chrome-bar-hover transition-colors outline-hidden"
        >
          <Folder size={14} className="text-chrome-bar-fg-dim shrink-0" />
          <span className="text-sm font-medium text-chrome-bar-fg truncate max-w-[180px]">
            {contract ? contract.title : 'Vyberte akci'}
          </span>
          <ChevronDown size={12} className="text-chrome-bar-fg-dim shrink-0 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[9999] w-[300px] p-0 bg-white rounded-md border-neutral-200 shadow-pop"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Hledat akci..."
            data-testid="contract-picker-search-input"
            onValueChange={(value) => debouncedFetch(value)}
          />
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-sm text-neutral-500">Načítání...</div>
            ) : items.length === 0 ? (
              <CommandEmpty>Žádné výsledky.</CommandEmpty>
            ) : (
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.phase}
                    value={item.phase}
                    data-testid={`contract-picker-item-${item.phase}`}
                    onSelect={() => handleSelect(item)}
                  >
                    <span className="flex-1 truncate">{item.title}</span>
                    {item.phase === phaseId && (
                      <Check size={14} className="shrink-0 text-primary-600" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
